import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { onAuthChange, updateUserProfile } from './services/authService';
import { db } from './config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { syncService } from './services/syncService';

// Pages - Lazy loaded for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage'));
const VerifyEmailTokenPage = lazy(() => import('./pages/VerifyEmailTokenPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TripDetailPage = lazy(() => import('./pages/TripDetailPage'));
const CreateTripPage = lazy(() => import('./pages/CreateTripPage'));
const InviteAcceptancePage = lazy(() => import('./pages/InviteAcceptancePage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));

// Components - Keep eagerly loaded as they're always needed
import Header from './components/Header';
import LoadingSpinner from './components/LoadingSpinner';
import NotificationBanner from './components/NotificationBanner';
import TutorialModal from './components/TutorialModal';
import OfflineIndicator from './components/OfflineIndicator';

// Protected Route Component
function ProtectedRoute({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Fetch user data from Firestore
  const fetchUserData = async (user) => {
    if (!user) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      setUser(user);
      await fetchUserData(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Re-fetch user data when location changes (e.g., after email verification)
  useEffect(() => {
    if (user && !loading) {
      fetchUserData(user);
    }
  }, [location.pathname]);

  if (loading) {
    return <LoadingSpinner />;
  }

  // If not authenticated, redirect to login with current path as redirect parameter
  if (!user) {
    return <Navigate to={`/login?redirect=${location.pathname}`} />;
  }

  // If user is authenticated but email is not verified, redirect to verification page
  // Exception: don't redirect if already on verification page or verify token page
  if (userData && !userData.emailVerified && location.pathname !== '/verify-email' && !location.pathname.startsWith('/verify/')) {
    return <Navigate to="/verify-email" />;
  }

  return children;
}

// Public Route Component (redirect if authenticated)
function PublicRoute({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthChange((user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  // If user is authenticated, check for redirect parameter
  if (user) {
    const searchParams = new URLSearchParams(location.search);
    const redirectUrl = searchParams.get('redirect');

    // If there's a redirect parameter, let the page handle it
    // Otherwise, redirect to dashboard
    if (redirectUrl) {
      return children;
    }
    return <Navigate to="/dashboard" />;
  }

  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      setUser(user);

      // Check if user needs to see tutorial
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('Tutorial check:', {
              hasSeenTutorial: userData.hasSeenTutorial,
              emailVerified: userData.emailVerified,
              willShow: !userData.hasSeenTutorial && userData.emailVerified
            });
            // Show tutorial if user hasn't seen it and email is verified
            if (!userData.hasSeenTutorial && userData.emailVerified) {
              console.log('🎬 Showing tutorial modal');
              setShowTutorial(true);
            }
          }
        } catch (error) {
          console.error('Error checking tutorial status:', error);
        }

        // Sync trips to offline storage when user logs in
        console.log('Syncing trips to offline storage...');
        syncService.syncAllTripsToLocal(user.uid).catch(error => {
          console.error('Error syncing trips on login:', error);
        });
      }

      setLoading(false);
    });

    // Listen for service worker sync requests
    const handleSyncRequest = () => {
      if (user) {
        console.log('Service worker requested sync');
        syncService.syncPendingChanges().catch(error => {
          console.error('Error syncing on SW request:', error);
        });
      }
    };

    window.addEventListener('sw-sync-request', handleSyncRequest);

    return () => {
      unsubscribe();
      window.removeEventListener('sw-sync-request', handleSyncRequest);
    };
  }, []);

  const handleTutorialComplete = async () => {
    if (user) {
      try {
        await updateUserProfile(user.uid, { hasSeenTutorial: true });
      } catch (error) {
        console.error('Error marking tutorial as seen:', error);
      }
    }
    setShowTutorial(false);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {user && <Header user={user} onShowTutorial={() => setShowTutorial(true)} />}
        {user && <NotificationBanner userId={user.uid} />}

        <div className="flex-1">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <LandingPage />
                  </PublicRoute>
                }
              />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <ForgotPasswordPage />
              }
            />
            <Route
              path="/reset-password/:token"
              element={
                <ResetPasswordPage />
              }
            />

            {/* Email Verification Routes */}
            <Route
              path="/verify-email"
              element={
                <ProtectedRoute>
                  <EmailVerificationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/verify/:token"
              element={
                <ProtectedRoute>
                  <VerifyEmailTokenPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trips/new"
              element={
                <ProtectedRoute>
                  <CreateTripPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trips/:id"
              element={
                <ProtectedRoute>
                  <TripDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/itinerary/:id"
              element={
                <ProtectedRoute>
                  <TripDetailPage isItineraryView={true} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invite/:inviteCode"
              element={
                <ProtectedRoute>
                  <InviteAcceptancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </Suspense>
        </div>

        {/* Tutorial Modal */}
        {showTutorial && (
          <TutorialModal
            onClose={() => setShowTutorial(false)}
            onComplete={handleTutorialComplete}
          />
        )}

        {/* Offline Indicator */}
        {user && <OfflineIndicator />}
      </div>
    </Router>
  );
}

export default App;
